/**
 * A quiet chip at the top of the screen while the device is offline.
 *
 * The signal is TanStack Query's `onlineManager`, which T11 feeds from
 * `@capacitor/network` (`data/online.ts`) — the same signal that pauses
 * queries and the workout outbox, so the chip never disagrees with what the
 * app is actually doing. Colours are `warning` / `warning-surface`, kept by
 * plan D10 for exactly this.
 *
 * The live region is always mounted and only its content changes, so a
 * screen reader announces the change instead of missing a region that
 * appeared already filled.
 */
import {
  "shell.offline_detail" as offlineDetail,
  "shell.offline" as offlineLabel,
} from "@repo/i18n";
import { onlineManager } from "@tanstack/react-query";
import { WifiOff } from "lucide-react";
import { useSyncExternalStore } from "react";
import { cx } from "../../lib/cx";
import { Icon } from "../../ui/controls";

function subscribe(onChange: () => void): () => void {
  return onlineManager.subscribe(onChange);
}

function isOnline(): boolean {
  return onlineManager.isOnline();
}

export function useIsOnline(): boolean {
  return useSyncExternalStore(subscribe, isOnline, isOnline);
}

export function OfflineIndicator({ className }: { className?: string }) {
  const online = useIsOnline();
  return (
    <div
      aria-live="polite"
      className={cx(
        "pointer-events-none absolute inset-x-0 top-2 z-20 flex justify-center px-4",
        className
      )}
      data-offline-indicator
      role="status"
    >
      {online ? null : (
        <span
          className="flex items-center gap-1.5 rounded-full bg-warning-surface px-3 py-1.5 font-semibold text-warning text-xs leading-none shadow"
          title={offlineDetail()}
        >
          <Icon icon={WifiOff} size={16} />
          {offlineLabel()}
          <span className="sr-only">{offlineDetail()}</span>
        </span>
      )}
    </div>
  );
}
